import {
  Button,
  Flex,
  Checkbox,
  EditableText,
  Pill,
  Input,
  Text,
  Menu,
  BigInfoIcon
} from "@theorgchartdev/storybook";
import { FilterReportsActions } from "./FilterReportsActions";
import { useFilterController } from "../useFilterController";

import { appstr } from "@coreapp/utils/appstr";
import { isInReporting } from "@coreapp/utils/isInReporting";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { useSharedFilterController, type SharedFilterDoc } from "../useSharedFilterController";
import { QSharedFilterRFC } from "@coreapp/CoreApp/QSharedFilterRFC";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { confirmDeleteShared } from "@/appComponents/Navigation/RightSidebar/Utils/confirmDeleteShared";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import {
  chartModeTestId,
  planningTestId
} from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type InteractiveFilterProps = {
  filterCtrl: ReturnType<typeof useFilterController>;
  onEditSharedFilter?: (filter: SharedFilterDoc) => void;
  onOwnerActiveCountChange?: (count: number) => void;
  onViewDetails?: (data: any) => void;
  onOpenEdit?: () => void;
};

export const InteractiveFilter = (props: InteractiveFilterProps) => {
  const { filterCtrl, onEditSharedFilter, onOwnerActiveCountChange, onViewDetails, onOpenEdit } =
    props;
  const { canShareFilters: canShare } = usePermissionCheck();
  const { addToast } = useToast() as any;

  const [shareModalState, setShareModalState] = useState<{
    show: boolean;
    block: QFilterBlock | null;
    blockIndex: number | null;
    sharedDoc: SharedFilterDoc | null;
  }>({ show: false, block: null, blockIndex: null, sharedDoc: null });

  const {
    sharedFilters: mySharedList,
    isBeingShared = () => false,
    fetchSharedItems,
    deleteShared
  } = useSharedFilterController();
  const [ownerEnabledIds, setOwnerEnabledIds] = useState<Set<string>>(new Set());
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    if (canShare) {
      fetchSharedItems("filter");
    }
  }, [canShare]);

  // Clear stale checkbox state when the current chart closes.
  useTopic("chartDocumentClosed", () => {
    setOwnerEnabledIds(new Set());
    onOwnerActiveCountChange?.(0);
  });

  // Owner's shared docs — displayed in "My filters" tab
  const ownerSharedDocs = useMemo(
    () => mySharedList.filter(f => f.userRole === "owner"),
    [mySharedList]
  );

  // Search and sort — personal blocks with their original indices + owner shared docs
  const { sortedPersonalBlocks, sortedOwnerDocs } = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let personalWithIndex = filterCtrl.filterBlocks.map((block, index) => ({ block, index }));
    let ownerDocs = [...ownerSharedDocs];

    if (searchTerm) {
      personalWithIndex = personalWithIndex.filter(({ block }) =>
        (block.GetName() || "").toLowerCase().includes(term)
      );
      ownerDocs = ownerDocs.filter(d => (d.definition?.name || "").toLowerCase().includes(term));
    }

    personalWithIndex.sort((a, b) => {
      const nameA = (a.block.GetName() || "").toLowerCase();
      const nameB = (b.block.GetName() || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    ownerDocs.sort((a, b) => {
      const nameA = (a.definition?.name || "").toLowerCase();
      const nameB = (b.definition?.name || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return { sortedPersonalBlocks: personalWithIndex, sortedOwnerDocs: ownerDocs };
  }, [filterCtrl.filterBlocks, ownerSharedDocs, searchTerm, sortAsc]);

  // Restore toggle state from filterMgr on mount/remount
  useEffect(() => {
    if (ownerSharedDocs.length === 0) return;
    const doc = GetDoc();
    const filterMgr = doc?.GetFilterMgr();
    if (!filterMgr) return;

    const contextId = filterMgr.GetContextId();
    const sharedCtxId = `shared_${contextId}`;
    const activeBlocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];
    const activeDocIds = new Set(activeBlocks.map((b: any) => b._sharedDocId));

    const ids = new Set<string>();
    ownerSharedDocs.forEach(d => {
      if (activeDocIds.has(d._id)) ids.add(d._id);
    });
    if (ids.size > 0) {
      setOwnerEnabledIds(ids);
      onOwnerActiveCountChange?.(ids.size);
    }
  }, [ownerSharedDocs]);

  // Toggle handler for owner's shared docs
  const handleOwnerToggle = useCallback(
    (sharedDoc: SharedFilterDoc) => {
      const doc = GetDoc();
      const wsMgr = GetWSMgr();
      const filterMgr = doc?.GetFilterMgr();
      if (!filterMgr || !wsMgr) return;

      const contextId = filterMgr.GetContextId();
      const sharedCtxId = `shared_${contextId}`;

      const next = new Set(ownerEnabledIds);
      if (next.has(sharedDoc._id)) next.delete(sharedDoc._id);
      else next.add(sharedDoc._id);
      setOwnerEnabledIds(next);
      onOwnerActiveCountChange?.(next.size);

      // Preserve non-owner shared blocks, rebuild only owner blocks
      const existingBlocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];
      const ownerDocIds = new Set(ownerSharedDocs.map(d => d._id));
      const nonOwnerBlocks = existingBlocks.filter((b: any) => !ownerDocIds.has(b._sharedDocId));

      const ownerBlocks = ownerSharedDocs
        .filter(d => next.has(d._id))
        .map(d => {
          const block = new QFilterBlock();
          block.LoadFromJSON({ ...d.definition, contextId: sharedCtxId, enabled: true });
          (block as any)._sharedDocId = d._id;
          return block;
        });

      filterMgr.SetFilterBlocks(sharedCtxId, [...nonOwnerBlocks, ...ownerBlocks]);

      wsMgr.ApplyChartFilters();
      if (doc && !doc.IsCustomChart()) {
        const scm = wsMgr.GetSubChartMgr();
        const curSubChart = scm.GetCurrentSubChart();
        const topBoxObj = curSubChart.GetTopDBObj();
        const topBoxId = topBoxObj?.GetObjectRecordID();
        scm.PurgeLoadCache();
        doc.DoChartDataQuery(topBoxId);
      }
      ReRenderCanvas();
    },
    [ownerEnabledIds, ownerSharedDocs]
  );

  const handleDeleteSharedFilter = useCallback(
    (docId: string) => {
      const theDoc = GetDoc();
      const filterMgr = theDoc?.GetFilterMgr();
      if (filterMgr) {
        const contextId = filterMgr.GetContextId();
        const sharedCtxId = `shared_${contextId}`;
        const blocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];
        const remaining = blocks.filter((b: any) => b._sharedDocId !== docId);
        filterMgr.SetFilterBlocks(sharedCtxId, remaining);
        const wsMgr = GetWSMgr();
        if (wsMgr) {
          wsMgr.ApplyChartFilters();
          ReRenderCanvas();
        }
      }
      setOwnerEnabledIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        onOwnerActiveCountChange?.(next.size);
        return next;
      });
      return deleteShared(docId).then(success => {
        if (success) fetchSharedItems("filter");
        return success;
      });
    },
    [deleteShared, fetchSharedItems]
  );

  // After sharing a personal block: delete personal copy + refetch shared list
  const handleShareComplete = () => {
    if (shareModalState.blockIndex !== null) {
      filterCtrl.handleBlockRemove(shareModalState.blockIndex)();
    }
    fetchSharedItems("filter");
  };

  const closeShareModal = () =>
    setShareModalState({ show: false, block: null, blockIndex: null, sharedDoc: null });

  const getName = (block: QFilterBlock) => {
    let name = block.GetName();

    // Ensure block has a name (for legacy filters)
    if (!name) {
      block.SetName(filterCtrl.getUniqueBlockName());
    }

    return name;
  };

  const getRulesFromBlock = (block: QFilterBlock) => {
    return block.GetRules?.() ?? (block as any).fFilterRuleList ?? [];
  };

  const getRulesFromDefinition = (definition: any) => {
    const block = new QFilterBlock();
    block.LoadFromJSON(definition);
    return getRulesFromBlock(block);
  };

  const wsMgr = GetWSMgr();
  const currentUserDisplayName = wsMgr?.GetUserInfo()?.GetName?.() || wsMgr?.GetUserName() || "";

  const buildPersonalMenuOptions = (block: QFilterBlock, index: number) => [
    {
      label: appstr("Copy filter"),
      value: "copy",
      icon: "copy" as const,
      dataTestId: chartModeTestId("filter", "filter-item-copy-btn"),
      clickHandler: () => {
        filterCtrl.handleCopyBlock(block.OutputToJSON());
        addToast({
          ...createBoldToastMessage("Entity copy created", { entity: getName(block) ?? "" }),
          status: TOAST_STATUS.SUCCESS
        });
      }
    },
    {
      label: appstr("Filter details"),
      value: "details",
      icon: "circle-info" as const,
      dataTestId: chartModeTestId("filter", "filter-item-details-btn"),
      clickHandler: () =>
        onViewDetails?.({
          name: getName(block) || "",
          ownerDisplayName: currentUserDisplayName,
          isShared: false,
          userRole: "owner",
          rules: getRulesFromBlock(block),
          sourceTab: "my",
          shareProps: {
            definition: block.OutputToJSON?.() || {},
            filterRef: block,
            onShareComplete: () => {
              filterCtrl.handleBlockRemove(index)();
              fetchSharedItems("filter");
            }
          },
          onCopy: () => filterCtrl.handleCopyAndEditBlock(block.OutputToJSON()),
          onEdit: () => filterCtrl.handleStartEditing(block, index),
          onDelete: () => filterCtrl.handleBlockRemove(index)()
        })
    }
  ];

  const buildOwnerMenuOptions = (doc: SharedFilterDoc) => [
    {
      label: appstr("Copy filter"),
      value: "copy",
      icon: "copy" as const,
      dataTestId: chartModeTestId("filter", "my-shared-filter-item-copy-btn"),
      clickHandler: () => {
        filterCtrl.handleCopyBlock(doc.definition);
        addToast({
          ...createBoldToastMessage("Entity copy created", {
            entity: doc.definition?.name ?? ""
          }),
          status: TOAST_STATUS.SUCCESS
        });
      }
    },
    {
      label: appstr("Filter details"),
      value: "details",
      icon: "circle-info" as const,
      dataTestId: chartModeTestId("filter", "my-shared-filter-item-details-btn"),
      clickHandler: () => {
        const sharedBlock = new QFilterBlock();
        sharedBlock.LoadFromJSON(doc.definition);
        onViewDetails?.({
          name: doc.definition?.name || "",
          ownerDisplayName: currentUserDisplayName,
          isShared: isBeingShared(doc._id),
          userRole: "owner",
          rules: getRulesFromDefinition(doc.definition),
          sourceTab: "my",
          shareProps: {
            definition: doc.definition,
            filterRef: sharedBlock,
            sharedDoc: doc,
            onShareComplete: () => fetchSharedItems("filter")
          },
          onCopy: () => filterCtrl.handleCopyAndEditBlock(doc.definition),
          onEdit: () => {
            onOpenEdit?.();
            onEditSharedFilter?.(doc);
          },
          onDelete: () => handleDeleteSharedFilter(doc._id)
        });
      }
    }
  ];

  const noFilters = filterCtrl.filterBlocks.length === 0 && ownerSharedDocs.length === 0;

  return (
    <Flex direction={"column"} height={"100%"}>
      <Flex className="sidebar-header-buttons" direction="column" gap="8px" padding="16px">
        <Text size="small">{appstr("My filters info")}</Text>
        <Button
          dataTestId={planningTestId("filter", "add-btn") ?? "filter--add-btn"}
          modifier={"secondary"}
          size={"small"}
          iconProps={{
            name: "plus-solid"
          }}
          onClick={() => {
            onOpenEdit?.();
            filterCtrl.handleAddBlock();
          }}
        >
          {appstr("Create filter")}
        </Button>
      </Flex>
      {(filterCtrl.filterBlocks.length > 0 || ownerSharedDocs.length > 0) && (
        <Flex className="sidebar-search-row" gap="8px" alignItems="center" padding="0 16px 8px">
          <Input
            placeholder={appstr("Search filters")}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            size="small"
            dataTestId="my-filters-search"
          />
          <Button
            iconOnly
            size="small"
            modifier="tertiary"
            iconProps={{ name: "arrow-down-a-z" }}
            tooltipProps={{ message: appstr("Sort") }}
            onClick={() => setSortAsc(prev => !prev)}
            dataTestId={chartModeTestId("filter", "my-filters-sort-toggle")}
          />
        </Flex>
      )}
      <Flex
        className={"ocn-list-scrollbar"}
        flex={"1"}
        direction={"column"}
        overflow={"auto"}
        padding={"0 16px 16px"}
      >
        {/* Personal (unshared) blocks */}
        {sortedPersonalBlocks.map(({ block, index }) => {
          const personalMenuOpen = activeMenuId === `personal-${index}`;
          return (
            <Flex key={`personal-${index}`} className="ocn-filter-block-list-item">
              <ActionListItem
                padding={"0px"}
                dataTestId={`filter-block-item--${index}`}
                actions={[
                  {
                    iconName: "ellipsis-vertical" as const,
                    active: personalMenuOpen,
                    content: (
                      <Menu
                        fillWidth={false}
                        popoverProps={{
                          onOpen: () => setActiveMenuId(`personal-${index}`),
                          onClose: () => setActiveMenuId(null)
                        }}
                        trigger={
                          <Button
                            modifier="tertiary"
                            iconOnly
                            size="small"
                            iconProps={{ name: "ellipsis-vertical" }}
                            dataTestId={chartModeTestId("filter", "filter-item-menu-btn")}
                          />
                        }
                        options={buildPersonalMenuOptions(block, index)}
                        position="bottom-end"
                        optionSize="medium"
                        listWidth="medium"
                      />
                    )
                  },
                  ...(canShare
                    ? [
                        {
                          iconName: "share-nodes" as const,
                          active: personalMenuOpen,
                          tooltipProps: { message: appstr("Share filter") },
                          dataTestId: chartModeTestId("filter", "filter-item-share-btn"),
                          onClick: () =>
                            setShareModalState({
                              show: true,
                              block,
                              blockIndex: index,
                              sharedDoc: null
                            })
                        }
                      ]
                    : []),
                  {
                    iconName: "pen-to-square" as const,
                    active: personalMenuOpen,
                    tooltipProps: { message: appstr("Edit filter") },
                    onClick: () => {
                      onOpenEdit?.();
                      filterCtrl.handleStartEditing(block, index);
                    },
                    dataTestId:
                      planningTestId("filter", "filter-item-edit-btn") ??
                      `filter-block-item--${index}-edit-btn`
                  },
                  {
                    iconName: "trash-can" as const,
                    active: personalMenuOpen,
                    dataTestId:
                      planningTestId("filter", "filter-item-remove-btn") ?? `filter--remove-btn`,
                    iconProps: { color: "error" },
                    tooltipProps: { message: appstr("Delete filter") },
                    onClick: () => {
                      const name = getName(block) ?? "";
                      filterCtrl.handleBlockRemove(index)();
                      addToast({
                        ...createBoldToastMessage("Entity deleted", { entity: name }),
                        status: TOAST_STATUS.SUCCESS
                      });
                    }
                  }
                ]}
                contentBefore={
                  <React.Fragment>
                    <Checkbox
                      id={`filter-enable-group-${index}`}
                      checked={block.GetEnabled()}
                      size={"medium"}
                      onToggle={() => filterCtrl.handleBlockToggle(index)}
                      data-testid={chartModeTestId("filter", "filter-item-checkbox")}
                    />
                    <EditableText
                      className="oc-fill-width"
                      bold={block.GetEnabled()}
                      text={getName(block) ?? ""}
                      onChange={text => {
                        filterCtrl.handleBlockNameChange(index, text);
                        addToast({
                          ...createBoldToastMessage("Entity rule renamed", { entity: text }),
                          status: TOAST_STATUS.SUCCESS
                        });
                      }}
                      validatorFn={text =>
                        filterCtrl.handleNameChangeValidation(getName(block) ?? "", text)
                      }
                      dataTestId={chartModeTestId("filter", "filter-item-rename")}
                    />
                  </React.Fragment>
                }
              />
            </Flex>
          );
        })}

        {/* Owner's shared docs — always show latest shared definition */}
        {sortedOwnerDocs.map(doc => {
          const ownerMenuOpen = activeMenuId === `shared-${doc._id}`;
          return (
            <Flex key={`shared-${doc._id}`} className="ocn-filter-block-list-item">
              <ActionListItem
                padding={"0px"}
                dataTestId={`filter-block-item--shared-${doc._id}`}
                actions={[
                  {
                    iconName: "ellipsis-vertical" as const,
                    active: ownerMenuOpen,
                    content: (
                      <Menu
                        fillWidth={false}
                        popoverProps={{
                          onOpen: () => setActiveMenuId(`shared-${doc._id}`),
                          onClose: () => setActiveMenuId(null)
                        }}
                        trigger={
                          <Button
                            modifier="tertiary"
                            iconOnly
                            size="small"
                            iconProps={{ name: "ellipsis-vertical" }}
                            dataTestId={chartModeTestId("filter", "my-shared-filter-item-menu-btn")}
                          />
                        }
                        options={buildOwnerMenuOptions(doc)}
                        position="bottom-end"
                        optionSize="medium"
                        listWidth="medium"
                      />
                    )
                  },
                  {
                    iconName: "share-nodes" as const,
                    active: ownerMenuOpen,
                    tooltipProps: { message: appstr("Share filter") },
                    dataTestId: chartModeTestId("filter", "my-shared-filter-item-share-btn"),
                    onClick: () =>
                      setShareModalState({
                        show: true,
                        block: null,
                        blockIndex: null,
                        sharedDoc: doc
                      })
                  },
                  {
                    iconName: "pen-to-square" as const,
                    dataTestId:
                      planningTestId("filter", "my-shared-filter-item-edit-btn") ??
                      `shared-filter--edit-btn`,
                    active: ownerMenuOpen,
                    tooltipProps: { message: appstr("Edit filter") },
                    onClick: () => {
                      onOpenEdit?.();
                      onEditSharedFilter?.(doc);
                    }
                  },
                  {
                    iconName: "trash-can" as const,
                    active: ownerMenuOpen,
                    dataTestId:
                      planningTestId("filter", "my-shared-filter-item-remove-btn") ??
                      `shared-filter--remove-btn`,
                    iconProps: { color: "error" },
                    tooltipProps: { message: appstr("Delete filter") },
                    onClick: () =>
                      confirmDeleteShared(GetWSMgr(), "filter", () => {
                        handleDeleteSharedFilter(doc._id).then(success => {
                          if (!success) {
                            addToast({
                              message: appstr("Shared filter delete error"),
                              status: TOAST_STATUS.ERROR,
                              timeoutMS: 5000
                            });
                            return;
                          }
                          addToast({
                            ...createBoldToastMessage("Entity deleted", {
                              entity: doc.definition?.name ?? ""
                            }),
                            status: TOAST_STATUS.SUCCESS
                          });
                        });
                      })
                  }
                ]}
                contentBefore={
                  <React.Fragment>
                    <Checkbox
                      id={`shared-filter-enable-${doc._id}`}
                      checked={ownerEnabledIds.has(doc._id)}
                      size={"medium"}
                      onToggle={() => handleOwnerToggle(doc)}
                      data-testid={chartModeTestId("filter", "my-shared-filter-item-checkbox")}
                    />
                    <div style={{ flex: 1, minWidth: 0 }} className="oc-overflow-text-tooltip">
                      <EditableText
                        bold={ownerEnabledIds.has(doc._id)}
                        text={doc.definition?.name || ""}
                        onChange={text => {
                          const wsMgr = GetWSMgr();
                          if (wsMgr) {
                            QSharedFilterRFC.UpdateSharedFilter(wsMgr, doc._id, {
                              ...doc.definition,
                              name: text
                            })
                              .then((raw: any) => {
                                const response = typeof raw === "string" ? JSON.parse(raw) : raw;
                                if (!response?.success) {
                                  addToast({
                                    message:
                                      response?.message || appstr("Shared filter update error"),
                                    status: TOAST_STATUS.ERROR,
                                    timeoutMS: 5000
                                  });
                                  return;
                                }
                                fetchSharedItems("filter");
                                addToast({
                                  ...createBoldToastMessage("Entity rule renamed", {
                                    entity: text
                                  }),
                                  status: TOAST_STATUS.SUCCESS
                                });
                              })
                              .catch((err: any) => {
                                console.error("Failed to rename:", err);
                                addToast({
                                  message: appstr("Shared filter update error"),
                                  status: TOAST_STATUS.ERROR,
                                  timeoutMS: 5000
                                });
                              });
                          }
                        }}
                        validatorFn={text =>
                          filterCtrl.handleNameChangeValidation(doc.definition?.name || "", text)
                        }
                        dataTestId={chartModeTestId("filter", "my-shared-filter-item-rename")}
                      />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {isBeingShared(doc._id) && (
                        <Pill label={appstr("Shared")} color="info" size="compact" />
                      )}
                    </div>
                  </React.Fragment>
                }
              />
            </Flex>
          );
        })}

        {/* No filters state */}
        {noFilters && (
          <BigInfoIcon name="filter" message={appstr("No filters created yet")} color="brand" />
        )}
      </Flex>
      {isInReporting() && <FilterReportsActions />}
      {canShare &&
        (() => {
          if (shareModalState.sharedDoc) {
            const sharedBlock = new QFilterBlock();
            sharedBlock.LoadFromJSON(shareModalState.sharedDoc.definition);
            return (
              <ShareFilterModal
                show={shareModalState.show}
                onClose={closeShareModal}
                filterType="filter"
                definition={shareModalState.sharedDoc.definition}
                filterName={shareModalState.sharedDoc.definition?.name || ""}
                filterRef={sharedBlock}
                onShareComplete={() => fetchSharedItems("filter")}
                editMode={true}
                existingFilterId={shareModalState.sharedDoc._id}
                existingSharedWith={shareModalState.sharedDoc.sharedWith}
              />
            );
          }
          return (
            <ShareFilterModal
              show={shareModalState.show}
              onClose={closeShareModal}
              filterType="filter"
              definition={shareModalState.block?.OutputToJSON?.() || {}}
              filterName={shareModalState.block?.GetName?.() || ""}
              filterRef={shareModalState.block}
              onShareComplete={handleShareComplete}
            />
          );
        })()}
    </Flex>
  );
};
